import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	Calendar,
	CalendarHandle,
	CalendarViewKind,
	SliderActionOptions,
	useCalendar
} from "client/Components";
import { EventList } from "../EventList/EventList";
import styles from "./CalendarView.module.less";
import { AppointmentItemModel, DateAsNumber } from "client_legacy/Workspace/LiveInformation/Calendar/provider";
import classNames from "classnames";
import {
	Appointment,
	DateToDisplay,
	getAppointmentsForDay
} from "../CalendarLiveTileContent";
import { localize } from "client_legacy/ndaw/culture";
import {
	useToday,
	useValueEqualMemo
} from "client/Hooks";
import { isNil } from "client/Misc/utils";
import { unique } from "client_legacy/ndaw/arrayHelpers";
import { TimeZone } from "client/DateAndTime/timezones";
import {
	addDays,
	calculateDayDifference,
	getIsToday,
	getStartOfWeek,
	isBetween
} from "client/DateAndTime/dateTimeUtils";
import { CalendarDestinationApp, DestinationWindow } from "client/Api/Apps/common";
import { convertToDateOnly } from "client/DateAndTime/dateUtils";

export type CalendarViewViewProps = {
	appointmentsPerDates: Map<number, readonly AppointmentItemModel[]>;
	errorMessage: string | nil;
	shouldShowLoadingIndicator: boolean;
	onDatesChanged(dates: readonly DateAsNumber[]): void;
	timezone: TimeZone;
	destinationApp: CalendarDestinationApp;
	destinationWindow: DestinationWindow;
	onListItemClick(): void;
	calendarId: string;
};

export type EventListDateOffset = {
	date: Date;
	offset: number;
};

export const CalendarView = (props: CalendarViewViewProps): ReactElement => {
	const {
		appointmentsPerDates,
		errorMessage,
		shouldShowLoadingIndicator,
		onDatesChanged,
		timezone,
		destinationApp,
		destinationWindow,
		onListItemClick,
		calendarId
	} = props;

	const today: Date = useToday({ timezone });

	const [value, setValue] = useState<Date>(today);
	const [dateToScrollTo, setDateToScrollTo] = useState<Date | null>(value);

	const calendarRef: RefObject<CalendarHandle | null> = useRef<CalendarHandle>(null);

	const {
		view,
		onViewChange,
		viewRange
	} = useCalendar(
		CalendarViewKind.WeekView,
		value,
		timezone
	);

	const datesWithEvent = useMemo(
		(): readonly Date[] => {
			const dates: Date[] = [];

			appointmentsPerDates.forEach((appointments: readonly AppointmentItemModel[], day: number) => {
				if (appointments.length > 0) {
					dates.push(new Date(day));
				}
			});

			return dates;
		},
		[appointmentsPerDates]
	);

	const stableValue = useValueEqualMemo(
		(): Date => value,
		[value]
	);

	const stableDateToScrollTo = useValueEqualMemo(
		(): Date | null => dateToScrollTo,
		[dateToScrollTo]
	);

	const eventListRange = useMemo(
		(): readonly DateAsNumber[] => {
			const startOfWeek: Date = getStartOfWeek(stableValue, timezone);
			const endOfWeek: Date = addDays(
				startOfWeek,
				7,
				timezone
			);

			return getDatesInRange(
				startOfWeek,
				endOfWeek,
				timezone
			);
		},
		[stableValue, timezone]
	);

	const isUpdating = useMemo(
		(): boolean => eventListRange.some(x => isNil(appointmentsPerDates.get(x))),
		[appointmentsPerDates, eventListRange]
	);

	const datesToDisplay = useMemo(
		(): DateToDisplay[] => convertAppointmentsToDatesToDisplay(
			eventListRange.map(x=> new Date(x)),
			unique(
				[...appointmentsPerDates.values()].flat(),
				x => x.uniqueId
			),
			timezone
		),
		[
			appointmentsPerDates,
			eventListRange,
			timezone
		]
	);

	const sliderAction = useMemo(
		(): SliderActionOptions | undefined => {
			if (
				view.viewKind !== CalendarViewKind.WeekView
				&& view.viewKind !== CalendarViewKind.Month
			) {
				return undefined;
			}

			return {
				icon: view.viewKind === CalendarViewKind.Month ? "chevron-up" : "chevron-down",
				onExecute: () => {
					onViewChange({
						...view,
						viewKind: view.viewKind === CalendarViewKind.Month
							? CalendarViewKind.WeekView
							: CalendarViewKind.Month
					});

					if (view.viewKind === CalendarViewKind.Month) {
						setDateToScrollTo(stableValue);
					}
				}
			};
		},
		[
			view,
			stableValue,
			onViewChange
		]
	);

	const goToDateHandler = useCallback(
		(value: Date): void => {
			setValue(value);
			onViewChange({
				date: value,
				viewKind: CalendarViewKind.WeekView
			});

			setDateToScrollTo(value);

			calendarRef.current
				?.focusDate(
					convertToDateOnly(value, timezone)
				);
		},
		[onViewChange, timezone]
	);

	const dateChangeHandler = useCallback(
		(newValue: Date): void => {
			setValue(newValue);

			if (!isBetween(
				newValue,
				viewRange[0],
				viewRange[1]
			)) {
				onViewChange({
					date: newValue,
					viewKind: view.viewKind
				});
			}
		},
		[
			onViewChange,
			view.viewKind,
			viewRange
		]
	);

	useEffect(
		(): void => {
			let calendarViewRangeAsNumbers: readonly DateAsNumber[] = [];

			if (
				view.viewKind === CalendarViewKind.WeekView
				|| view.viewKind === CalendarViewKind.Month
			) {
				calendarViewRangeAsNumbers = getDatesInRange(
					viewRange[0],
					viewRange[1],
					timezone
				);
			}

			const datesToFetch: number[] = [...calendarViewRangeAsNumbers];

			eventListRange.forEach(
				x => {
					if (!datesToFetch.includes(x)) {
						datesToFetch.push(x);
					}
				}
			);

			onDatesChanged(datesToFetch);
		},
		[
			eventListRange,
			onDatesChanged,
			view.viewKind,
			viewRange,
			timezone
		]
	);

	return (
		<div className={styles.root}>
			<div
				className={
					classNames(
						styles.calendar,
						{
							[styles.calendar_fullHeight]: view.viewKind !== CalendarViewKind.WeekView
						}
					)
				}
			>
				<Calendar
					value={value}
					onChange={goToDateHandler}
					trackingId="calendar"
					view={view}
					onViewChange={onViewChange}
					eventDates={datesWithEvent}
					sliderAction={sliderAction}
					color="app"
					showWeekNumbers
					timezone={timezone}
					ref={calendarRef}
				/>
			</div>
			{view.viewKind === CalendarViewKind.WeekView && (
				<div className={styles.eventList}>
					<EventList
						dateToScrollTo={stableDateToScrollTo}
						onUnsetDateToScrollTo={() => setDateToScrollTo(null)}
						onDateChange={dateChangeHandler}
						datesToDisplay={datesToDisplay}
						errorMessage={errorMessage}
						trackingId="selected-week-events-list"
						isUpdating={isUpdating}
						shouldShowLoadingIndicator={shouldShowLoadingIndicator}
						timezone={timezone}
						destinationApp={destinationApp}
						destinationWindow={destinationWindow}
						onListItemClick={onListItemClick}
						calendarId={calendarId}
					/>
				</div>
			)}
		</div>
	);
};

CalendarView.displayName = "CalendarView";

export function getDatesInRange(
	from: Date,
	to: Date,
	timezone: TimeZone
): readonly DateAsNumber[] {
	const numberOfDays: number = calculateDayDifference(
		to,
		from,
		timezone
	);

	const result: DateAsNumber[] = [];

	for (let i = 0; i < numberOfDays; i++) {
		const date: Date = addDays(
			from,
			i,
			timezone
		);

		result.push(date.getTime());
	}

	return result;
}

function convertAppointmentsToDatesToDisplay(
	dates: readonly Date[],
	appointments: readonly AppointmentItemModel[],
	timezone: TimeZone
): DateToDisplay[] {
	return dates.map(d => {
		const selectedDateAppointments: readonly Appointment[] = getAppointmentsForDay(
			appointments,
			d,
			timezone
		);

		const isToday: boolean = getIsToday(d, timezone);

		const uniqueKey: string = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

		return selectedDateAppointments.length
			? {
				uniqueKey,
				date: {
					isToday,
					value: d
				},
				appointments: selectedDateAppointments,
				message: ""
			} : {
				uniqueKey,
				date: {
					isToday,
					value: d
				},
				appointments: [],
				message: localize("_NoEvents")
			};
	});
}
